import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { intersection } from 'lodash';

import type { AclAction } from '@sine/sine-core-api-types';
import { sortItem } from '@sine/sine-ui-components';

import mapToDropdownOptions from '../../helpers/mapToDropdownOptions';
import parseServerError from '../../helpers/parseServerError';
import notificationService from '../../notifications/notificationService';
import { QueryKeys } from '../../query.client';
import siteService, { type GetAllAdminSitesParams } from './site.service';

const CACHE_TIME = 10000; // 10 seconds

export const ADMIN_SITES_QUERY_CONFIG = {
  GLOBAL_MESSAGING: {
    withCheckinMethods: false,
    withLinkedSites: false,
    withVisitorTypes: false,
  },
  SITES_LIST_PAGE: {
    withCheckinMethods: true,
    withLinkedSites: true,
    withVisitorTypes: true,
  },
};

export function useSitesWithPermissionsQuery(permissions: Array<AclAction>, withHostProfileIds = false) {
  const {
    isFetching,
    isLoading,
    data = [],
    refetch,
  } = useQuery({
    enabled: !!permissions.length,
    queryKey: [QueryKeys.MY_SITES_WITH_PERMISSIONS, withHostProfileIds],
    queryFn: async () => siteService.getMySitesWithPermissions(withHostProfileIds),
    onError: (error: any) => {
      notificationService.addNotification({
        message: parseServerError(error),
        variant: 'danger',
      });
    },
    placeholderData: [],
    staleTime: CACHE_TIME,
    cacheTime: CACHE_TIME,
  });

  const { sites, siteOptions } = useMemo(() => {
    const permissibleSites = data
      .filter((s) => !!intersection(s.permissions, permissions).length)
      .sort((a, b) => sortItem(a.name, b.name));

    return {
      sites: permissibleSites,
      siteOptions: mapToDropdownOptions(permissibleSites),
    };
    // Explicitly use `...permissions` instead of `permissions` to avoid an infinite rerender loop.
    // Infinite rerender loop can happen because the `permissions` reference changes due to not being memoised by the callers,
    // and it's easier to handle here than memoise everywhere this hook is used.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, ...permissions]);

  return {
    isFetchingSites: isFetching,
    isLoadingSites: isLoading,
    sites,
    siteOptions,
    refetchSites: refetch,
  };
}

export function useAdminSites({
  withCheckinMethods,
  withLinkedSites,
  withVisitorTypes,
  contextTeamId,
}: GetAllAdminSitesParams) {
  const {
    isFetching,
    data = [],
    refetch,
  } = useQuery({
    enabled: !!contextTeamId,
    queryKey: [QueryKeys.ADMIN_SITES, withCheckinMethods, withLinkedSites, contextTeamId, withVisitorTypes],
    queryFn: async () =>
      siteService.getAdminSites({ withCheckinMethods, withLinkedSites, withVisitorTypes, contextTeamId }),
    onError: (error: any) => {
      notificationService.addNotification({
        message: parseServerError(error),
        variant: 'danger',
      });
    },
    placeholderData: [],
    keepPreviousData: true,
    staleTime: CACHE_TIME,
    cacheTime: CACHE_TIME,
  });

  return {
    isLoadingSites: isFetching,
    sites: data,
    refetchSites: refetch,
  };
}
